import { createContext, useContext } from "react";
import type { TenantCompany, TenantProject, TenantSessionResponse } from "../shared/interfaces";
export type { TenantCompany, TenantProject } from "../shared/interfaces";
export interface TenantScopeValue {
    mode: "legacy" | "canonical";
    authScope?: "legacy_org" | "project" | "company";
    discoveryState?: "pending" | "ready" | "failed";
    retryDiscovery?: () => void;
    companies: TenantCompany[];
    projects: TenantProject[];
    selectedCompany: TenantCompany | null;
    selectedProject: TenantProject | null;
    capabilities: ReadonlySet<string>;
    scopeKey: string | null;
    sessionMember?: TenantSessionResponse["member"];
    sessionOrganization?: TenantSessionResponse["organization"];
    session?: TenantSessionResponse["session"];
    /** Identity already fetched by this document's current legacy admission. */
    bootstrapIdentity?: Pick<TenantSessionResponse, "member" | "organization">;
    /** The person is Primepoint staff: they may list every project. */
    isPlatformIdentity: boolean;
    /** The selected project is admitted to Primepoint staff whose membership does not admit
     *  them (`acting_as_staff`): they act as staff there, never as a member. */
    actingAsStaff: boolean;
    /** The server's word (`/session/me`) that this legacy session is the Primepoint staff
     *  registry's, which holds no project: project pages ask for one, and a project-free page
     *  (`routeContract`) mounts a query client without project admission. */
    staffRegistrySession: boolean;
    /** The staff registry's organization, named to a Primepoint person in any session (null
     *  otherwise): the project switcher never offers it as a project. */
    staffRegistryOrganizationId: string | null;
    isMasterOrganization?: boolean;
    loading: boolean;
    scopeValidated: boolean;
    hasMoreProjects?: boolean;
    hasMoreCompanies?: boolean;
    projectsLoading?: boolean;
    companiesLoading?: boolean;
    loadMoreProjects?: () => Promise<void>;
    loadMoreCompanies?: () => Promise<void>;
    projectUrlsAvailable?: boolean;
    /** The project a copied link names (`useShareUrl`): the selected project on a project URL
     *  tab, else the published project this session is bound to, as on a native app. */
    linkProject?: { companySlug: string; projectSlug: string } | null;
    loadProjectChoices?: () => Promise<void>;
    projectHref?: (id: string) => string | null;
    /** Asks for a project's admission before it is chosen (a switcher row the pointer or focus
     *  rests on), so choosing it skips that request. Absent where a switch cannot use it. */
    prefetchProject?: (id: string) => void;
    /** A chosen project is waiting for its prefetched admission before the page leaves. */
    switching?: boolean;
    selectCompany: (id: string) => void;
    selectProject: (id: string) => void;
}
export const EMPTY_CAPABILITIES: ReadonlySet<string> = new Set();
export const EMPTY: TenantScopeValue = {
    mode: "legacy",
    companies: [],
    projects: [],
    selectedCompany: null,
    selectedProject: null,
    capabilities: EMPTY_CAPABILITIES,
    scopeKey: null,
    isPlatformIdentity: false,
    actingAsStaff: false,
    staffRegistrySession: false,
    staffRegistryOrganizationId: null,
    isMasterOrganization: false,
    loading: false,
    scopeValidated: false,
    selectCompany: () => undefined,
    selectProject: () => undefined,
};
export const TenantScopeContext = createContext<TenantScopeValue>(EMPTY);

export function useTenantScope(): TenantScopeValue {
    return useContext(TenantScopeContext);
}
